import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { isExpensesListPath, useCutoverNotice } from '@/expenses/CutoverNotice';
import { expensesGateOpen } from '@/expenses/ExpensesGate';
import { useExpensesPermissions } from '@/expenses/use-expenses-permissions';
import { useNewVersionNotice } from '@/pwa/NewVersionNotice';
import { useThemeNotice } from './FirstLoadNotice';
import type { ShellNotice } from './shell-notice';

function Details({ notice, details }: { readonly notice: ShellNotice; readonly details: string }) {
  const { t } = useTranslation('common');
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button type="button" variant="ghost" className="min-h-11 shrink-0 underline">
          {t('notice.details')}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="mx-auto max-w-xl rounded-t-xl p-4 pb-[calc(16px+env(safe-area-inset-bottom,0px))]"
      >
        <SheetTitle>{notice.label}</SheetTitle>
        <SheetDescription className="text-foreground">{details}</SheetDescription>
      </SheetContent>
    </Sheet>
  );
}

/**
 * The shell's one notice area (#956, audit M22): at most one notice at a time, the most recent
 * first (a new version, then the nexpense cutover, then the theme change), each on a single line
 * with «Dettagli» for the full text. Closing one shows the next. The cutover notice shows on the
 * expenses and notes lists only, and only once the expenses permissions are read and the module's
 * gate is open (#956 follow-up): the path alone would show it to someone the gate then sends away.
 * While they are being read on a list, nothing below the new version shows, so the theme notice
 * does not flash before the cutover notice replaces it. Before, the three stacked and took
 * ≈ 330 px of a phone's screen.
 */
export function NoticeArea({ newVersion }: { readonly newVersion: boolean }) {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();
  const version = useNewVersionNotice(newVersion);
  const onList = isExpensesListPath(pathname);
  const gate = expensesGateOpen(useExpensesPermissions());
  const cutover = useCutoverNotice(onList && gate === true);
  const theme = useThemeNotice();
  const pending = onList && gate === 'unknown';
  const notice = version ?? (pending ? null : (cutover ?? theme));
  if (notice === null) return null;
  const Icon = notice.icon;
  return (
    <section
      key={notice.id}
      aria-label={notice.label}
      role={notice.live ? 'status' : undefined}
      className="border-border bg-accent text-accent-foreground flex min-h-11 items-center gap-2 border-b px-[clamp(16px,2.4vw,24px)] text-sm print:hidden"
    >
      <Icon aria-hidden="true" className="text-brand size-4 shrink-0" />
      <p className="m-0 min-w-0 flex-1 truncate" title={notice.text}>
        {notice.text}
      </p>
      {notice.details === undefined ? null : <Details notice={notice} details={notice.details} />}
      {notice.action === undefined ? null : (
        // #951: the one thing to do about it is the primary.
        <Button type="button" className="min-h-11 shrink-0" onClick={notice.action.run}>
          {notice.action.label}
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="shrink-0"
        aria-label={t('dismiss')}
        onClick={notice.dismiss}
      >
        <X aria-hidden="true" />
      </Button>
    </section>
  );
}
